import { useCallback, useMemo, useState } from 'react';
import { Pressable, RefreshControl, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { ActivityIndicator } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import Chip from '@/components/General/Chip';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import SearchBox from '@/components/General/inputs/SearchBox';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import FollowUpHeader from '@/components/FollowUp/FollowUpHeader';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { canViewModule, isFreeOrg } from '@/utils/organizationEntitlements';
import { extractFollowUpCode } from '@/data/followUp';
import { parseTimeFrame } from '@/utils/dater';
import { getEnrollmentStatusMeta, getStopReasonLabel } from '@/utils/constants/followUp';

/**
 * /follow-up/paths — §25.5 two-view switch. Default view is ACTIVE
 * ENROLLMENTS (person, Path, current step, assignee, next action) with
 * server-backed search/filters and cursor pagination via
 * follow_up_list_enrollments — never a full-org fetch + JS filter.
 * The Path templates view lists flat definitions with step counts and
 * active enrollment counts plus the Automations explanation (a person can
 * start manually or be enrolled by an Automation — the link only renders
 * for users with 'View Paths').
 */

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'COMPLETED', label: 'Finished' },
    { value: 'CANCELLED', label: 'Stopped' },
    { value: 'ALL', label: 'All' },
];

/** Honest next-action text for a row's currentStep (§25.5). */
const nextActionText = (current) => {
    if (!current) return null;
    if (current.status === 'NEEDS_ATTENTION') {
        return current.attentionReason || 'Needs attention';
    }
    if (current.status === 'WAITING') {
        return current.availableAt ? `Starts ${parseTimeFrame(current.availableAt)}` : 'Scheduled';
    }
    // ACTIVE human work — expiration or explicit unlimited.
    return current.expiresAt ? `Expires ${parseTimeFrame(current.expiresAt)}` : 'No time limit';
};

const EnrollmentRow = ({ item, onPress }) => {
    const meta = getEnrollmentStatusMeta(item.status);
    const current = item.currentStep;
    const attention = !!item.needsAttention || current?.status === 'NEEDS_ATTENTION';
    return (
        <Pressable
            testID={`enrollment-row-${item.enrollmentID}`}
            accessibilityRole="button"
            onPress={onPress}
            style={styles.card}
        >
            <View style={styles.cardBody}>
                <View style={styles.titleRow}>
                    <Text style={styles.cardTitle} numberOfLines={1}>{item.personName || 'Unknown'}</Text>
                    <Chip icon={meta.icon} colorVariant={meta.colorVariant}>{meta.label}</Chip>
                </View>
                <Text style={styles.cardDesc} numberOfLines={1}>{item.pathName || 'Path'}</Text>
                <Text style={styles.cardMeta} numberOfLines={2}>
                    {[
                        current?.label ? `Now: ${current.label}` : null,
                        current?.assigneeName ? `→ ${current.assigneeName}` : null,
                        nextActionText(current),
                        item.stopReason ? getStopReasonLabel(item.stopReason) : null,
                        item.startedAt ? `Started ${parseTimeFrame(item.startedAt)}` : null,
                    ].filter(Boolean).join(' · ')}
                </Text>
            </View>
            {attention && (
                <Chip icon="alert-circle-outline" colorVariant="warning">
                    {current?.attentionReason || 'Needs attention'}
                </Chip>
            )}
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

const PathRow = ({ item, onPress }) => {
    const archived = !!item.isArchived;
    return (
        <Pressable
            testID={`path-row-${item.pathID}`}
            accessibilityRole="button"
            onPress={onPress}
            style={[styles.card, archived && styles.cardArchived]}
        >
            <View style={styles.iconWrap}>
                <Unicon name="git-branch-outline" size={16} colorVariant={archived ? 'textTertiary' : 'paths'} />
            </View>
            <View style={styles.cardBody}>
                <View style={styles.titleRow}>
                    <Text style={styles.cardTitle} numberOfLines={1}>{item.name}</Text>
                    {item.isActive === false && !archived && (
                        <Chip icon="pause-outline" colorVariant="textSecondary">Inactive</Chip>
                    )}
                    {archived && (
                        <Chip icon="archive-outline" colorVariant="textTertiary">Archived</Chip>
                    )}
                </View>
                {!!item.description && (
                    <Text style={styles.cardDesc} numberOfLines={2}>{item.description}</Text>
                )}
                <View style={styles.chipsRow}>
                    <Chip icon="list-outline" colorVariant="textSecondary">
                        {`${item.stepCount ?? 0} step${item.stepCount === 1 ? '' : 's'}`}
                    </Chip>
                    {(item.activeEnrollmentCount || 0) > 0 && (
                        <Chip icon="people-outline" colorVariant="paths">
                            {`${item.activeEnrollmentCount} enrolled`}
                        </Chip>
                    )}
                </View>
            </View>
            <Unicon name="chevron-forward-outline" size={16} colorVariant="textTertiary" />
        </Pressable>
    );
};

export default function FollowUpPathsScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const { entitlements } = useOrganizationEntitlements();

    const organizationID = loggedInUser?.organizationID;
    const canView = !!loggedInUser?.can?.('View Follow Up');
    const canManage = !!loggedInUser?.can?.('Manage Follow Up');
    // Automations live under 'View Paths' — the explanation renders for
    // everyone, the management link only for authorized users (§25.5).
    const canViewAutomations = !!loggedInUser?.can?.('View Paths') || !!loggedInUser?.isAdmin;
    const moduleAllowed = canViewModule(entitlements, 'paths');
    const gated = !!loggedInUser && canView && moduleAllowed;

    const [view, setView] = useState('enrollments'); // 'enrollments' | 'templates'
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('ACTIVE');
    const [pathFilter, setPathFilter] = useState(null); // {pathID, name}
    const [assigneeFilter, setAssigneeFilter] = useState(null); // person object
    const [needsAttentionOnly, setNeedsAttentionOnly] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const { data: paths = [], isLoading: pathsLoading } = Query.followUp.paths({
        organizationID,
        options: { enabled: gated && view === 'templates' },
    });

    // Server-side filter — serialized into the query key so combinations
    // never share pages. Status: Active/Finished/Stopped/All (no Paused).
    const enrollmentFilter = useMemo(() => {
        const f = { status: status || 'ACTIVE' };
        const trimmed = `${search || ''}`.trim();
        if (trimmed) f.search = trimmed;
        if (pathFilter?.pathID) f.pathID = pathFilter.pathID;
        if (assigneeFilter?.id) f.assigneePersonID = assigneeFilter.id;
        if (needsAttentionOnly) f.needsAttention = true;
        return f;
    }, [status, search, pathFilter?.pathID, assigneeFilter?.id, needsAttentionOnly]);

    const {
        data: enrollPages, isLoading: enrollLoading, isError, error, isRefetching,
        hasNextPage, fetchNextPage, isFetchingNextPage,
    } = Query.followUp.enrollments({
        organizationID,
        filter: enrollmentFilter,
        options: { enabled: gated && view === 'enrollments' },
    });

    const enrollments = useMemo(
        () => (enrollPages?.pages || []).flatMap((page) => page?.rows || []),
        [enrollPages]
    );

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.followUp.paths(organizationID) });
        queryClient.invalidateQueries({ queryKey: ['followUp', organizationID, 'enrollments'] });
    }, [queryClient, organizationID]);

    if (loggedInUser && (!canView || !moduleAllowed)) {
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={!moduleAllowed && isFreeOrg(entitlements)
                        ? 'Follow Up is not available on this plan'
                        : "You don't have permission to view Follow Up. Contact your Reach administrator to be added to the Follow Up team."}
                />
            </View>
        );
    }

    if (isError && view === 'enrollments') {
        const code = extractFollowUpCode(error) || error?.followUpCode;
        return (
            <View style={styles.deniedWrap}>
                <EmptyListPlaceholder
                    type="404"
                    spacing={40}
                    message={code === 'FOLLOW_UP_DENIED'
                        ? "You don't have permission to view Follow Up paths."
                        : 'Something went wrong loading enrollments. Pull to refresh to try again.'}
                />
            </View>
        );
    }

    const active = paths.filter((p) => !p.isArchived);
    const archived = paths.filter((p) => p.isArchived);
    const hasSecondaryFilters = !!(pathFilter || assigneeFilter || needsAttentionOnly);

    return (
        <View style={styles.container}>
            <ScrollView
                style={styles.scroll}
                contentContainerStyle={styles.content}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refresh} />}
            >
                <FollowUpHeader active="paths" canManage={canManage} />

                <View style={styles.switchRow} testID="paths-view-switch">
                    {[
                        { key: 'enrollments', label: 'Active enrollments', icon: 'people-outline' },
                        { key: 'templates', label: 'Path templates', icon: 'git-branch-outline' },
                    ].map((v) => (
                        <Pressable
                            key={v.key}
                            testID={`paths-view-${v.key}`}
                            accessibilityRole="button"
                            accessibilityState={{ selected: view === v.key }}
                            onPress={() => setView(v.key)}
                            style={[styles.switchBtn, view === v.key && styles.switchBtnActive]}
                        >
                            <Unicon name={v.icon} size={14} colorVariant={view === v.key ? 'paths' : 'textSecondary'} />
                            <Text style={[styles.switchText, view === v.key && styles.switchTextActive]}>{v.label}</Text>
                        </Pressable>
                    ))}
                </View>

                {view === 'enrollments' ? (
                    <>
                        <View style={styles.toolbar}>
                            <View style={styles.searchWrap}>
                                <SearchBox
                                    placeholder="Search people or paths..."
                                    value={search}
                                    onChangeText={setSearch}
                                    testID="paths-search"
                                />
                            </View>
                            <SolidButton
                                testID="paths-filter-toggle"
                                size="sm"
                                variant="outlined"
                                type={hasSecondaryFilters || filtersOpen ? 'paths' : 'secondary'}
                                iconName="filter-outline"
                                text="Filter"
                                onPress={() => setFiltersOpen((o) => !o)}
                            />
                        </View>
                        <View style={styles.chipsRow}>
                            {STATUS_OPTIONS.map((o) => (
                                <Chip
                                    key={o.value}
                                    testID={`paths-status-${o.value}`}
                                    colorVariant={status === o.value ? 'paths' : 'textSecondary'}
                                    selected={status === o.value}
                                    onPress={() => setStatus(o.value)}
                                >
                                    {o.label}
                                </Chip>
                            ))}
                        </View>
                        {filtersOpen && (
                            <View style={styles.filterPanel} testID="paths-filter-panel">
                                <SelectInput
                                    title="Path"
                                    placeholder="Any path"
                                    value={pathFilter?.pathID || null}
                                    options={(paths || []).filter((p) => !p.isArchived).map((p) => ({ value: p.pathID, label: p.name }))}
                                    onUpdate={(v) => {
                                        const id = typeof v === 'object' ? v?.value : v;
                                        setPathFilter(id ? { pathID: id, name: (paths || []).find((p) => p.pathID === id)?.name } : null);
                                    }}
                                />
                                <SheetSelector
                                    type="people"
                                    title="Assignee / connector"
                                    placeholder="Anyone"
                                    value={assigneeFilter}
                                    onChange={(p) => setAssigneeFilter(p?.id ? p : null)}
                                    testID="paths-assignee-filter"
                                />
                                <Pressable
                                    testID="paths-attention-filter"
                                    accessibilityRole="button"
                                    accessibilityState={{ selected: needsAttentionOnly }}
                                    onPress={() => setNeedsAttentionOnly((v) => !v)}
                                    style={styles.attentionRow}
                                >
                                    <Unicon
                                        name={needsAttentionOnly ? 'checkbox' : 'square-outline'}
                                        size={18}
                                        colorVariant={needsAttentionOnly ? 'paths' : 'textSecondary'}
                                    />
                                    <Text style={styles.attentionText}>Needs attention only</Text>
                                </Pressable>
                            </View>
                        )}

                        {enrollLoading ? (
                            <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                        ) : enrollments.length === 0 ? (
                            <EmptyListPlaceholder
                                spacing={30}
                                icon="people-outline"
                                message={status === 'ACTIVE'
                                    ? 'No active enrollments. Start someone on a Path from their Follow Up surface or with an Automation.'
                                    : 'No enrollments match this filter.'}
                            />
                        ) : (
                            <>
                                {enrollments.map((item) => (
                                    <EnrollmentRow
                                        key={item.enrollmentID}
                                        item={item}
                                        onPress={() => router.push(`/follow-up/enrollments/${item.enrollmentID}`)}
                                    />
                                ))}
                                {hasNextPage && (
                                    <View style={styles.footer}>
                                        {isFetchingNextPage ? (
                                            <ActivityIndicator />
                                        ) : (
                                            <SolidButton
                                                testID="paths-load-more"
                                                variant="outlined"
                                                type="secondary"
                                                size="sm"
                                                text="Load More"
                                                onPress={() => fetchNextPage()}
                                            />
                                        )}
                                    </View>
                                )}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {canManage && (
                            <View style={styles.actionsRow}>
                                <SolidButton
                                    testID="paths-new"
                                    size="sm"
                                    iconName="add"
                                    text="New Path"
                                    onPress={() => router.push('/follow-up/paths/new')}
                                />
                            </View>
                        )}

                        {/* §25.5 Automations explanation — the link is gated
                            on 'View Paths'; everyone else still sees the
                            plain-language note, never a dead link. */}
                        <View style={styles.automationNote} testID="paths-automations-note">
                            <Unicon name="flash-outline" size={15} colorVariant="paths" />
                            <Text style={styles.automationText}>
                                People can start a Path manually or be enrolled automatically using{' '}
                                {canViewAutomations ? (
                                    <Text
                                        testID="paths-automations-link"
                                        accessibilityRole="link"
                                        style={styles.automationLink}
                                        onPress={() => router.push('/automations')}
                                    >
                                        Automations
                                    </Text>
                                ) : 'Automations'}
                                .
                            </Text>
                        </View>

                        {pathsLoading ? (
                            <View style={styles.loadingWrap}><ActivityIndicator size="large" /></View>
                        ) : (
                            <>
                                {active.length === 0 && archived.length === 0 && (
                                    <EmptyListPlaceholder
                                        spacing={30}
                                        icon="git-branch-outline"
                                        message={canManage
                                            ? 'No paths yet. Create one to define a journey people can be started on.'
                                            : 'No paths are configured yet. A Follow Up manager can create them.'}
                                    />
                                )}
                                {active.map((item) => (
                                    <PathRow
                                        key={item.pathID}
                                        item={item}
                                        onPress={() => router.push(`/follow-up/paths/${item.pathID}`)}
                                    />
                                ))}
                                {archived.length > 0 && (
                                    <>
                                        <Text style={styles.sectionTitle}>Archived</Text>
                                        {archived.map((item) => (
                                            <PathRow
                                                key={item.pathID}
                                                item={item}
                                                onPress={() => router.push(`/follow-up/paths/${item.pathID}`)}
                                            />
                                        ))}
                                    </>
                                )}
                                {paths.length > 0 && (
                                    <Text style={styles.footerHint}>
                                        {`Last updated ${paths.map((p) => p.updatedAt).filter(Boolean).sort().slice(-1)[0] ? parseTimeFrame(paths.map((p) => p.updatedAt).filter(Boolean).sort().slice(-1)[0]) : 'recently'}`}
                                    </Text>
                                )}
                            </>
                        )}
                    </>
                )}
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: { flex: 1 },
    scroll: { flex: 1 },
    content: { padding: 16, paddingBottom: 60, gap: 12, maxWidth: 1180, width: '100%', alignSelf: 'center' },
    deniedWrap: { flex: 1, justifyContent: 'center' },
    switchRow: { flexDirection: 'row', gap: 8 },
    switchBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 14,
        paddingVertical: 8,
        borderRadius: 20,
        backgroundColor: colors.surfaceVariant,
        _web: { cursor: 'pointer' },
    },
    switchBtnActive: { backgroundColor: colors.pathsContainer },
    switchText: { fontSize: 13, fontFamily: fonts.medium, color: colors.textSecondary },
    switchTextActive: { color: colors.paths, fontFamily: fonts.bold },
    toolbar: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    searchWrap: { flex: 1, minWidth: 0 },
    chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    filterPanel: { gap: 4 },
    attentionRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 4 },
    attentionText: { fontSize: 14, fontFamily: fonts.medium, color: colors.text },
    actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
    automationNote: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: colors.pathsContainer,
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 10,
    },
    automationText: { flex: 1, fontSize: 13, color: colors.text, lineHeight: 18 },
    automationLink: { fontFamily: fonts.bold, color: colors.paths, _web: { cursor: 'pointer' } },
    loadingWrap: { padding: 40, alignItems: 'center' },
    sectionTitle: { fontSize: 13, fontFamily: fonts.heading, color: colors.textSecondary, marginTop: 6 },
    footer: { alignItems: 'center', paddingVertical: 8 },
    footerHint: { fontSize: 11, color: colors.textTertiary },
    card: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 12,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: colors.hover },
        },
    },
    cardArchived: { opacity: 0.7 },
    iconWrap: {
        width: 34,
        height: 34,
        borderRadius: 9,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardBody: { flex: 1, minWidth: 0, gap: 4 },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
    cardTitle: { fontSize: 15, fontFamily: fonts.heading, color: colors.text, flexShrink: 1 },
    cardDesc: { fontSize: 12, color: colors.textSecondary },
    cardMeta: { fontSize: 12, color: colors.textSecondary },
}));
